<template>
  <div class="work-instructions-container">
    <WorkInstructionsHeader
      @onAdd="onAddWorkInstructionPackage"
      @onImport="triggerImportFile"
      @onExport="exportCurrentPackageToExcel"
      @onDelete="onDeleteWorkInstructionPackage"
    />
    <input
      ref="importFileInput"
      type="file"
      accept=".xlsx,.xls"
      style="display: none"
      @change="importFromExcel"
    />
    <CreateWIPackageModal
      v-if="showAddWorkInstructionPackageModal"
      :job="currentJob"
      @close="showAddWorkInstructionPackageModal = false"
      @save="onSaveNewPackage"
    />
    <CreateWIModal
      v-if="showAddWIModal && currentPackage"
      :initialData="selectedWorkInstructionForEdit"
      :isEditMode="isEditingWorkInstruction"
      @close="showAddWIModal = false"
      @save="onSaveWorkInstruction"
      @onDelete="onDeleteWorkInstruction"
    />
    <WIStepModal
      v-if="showAddWIStepModal && currentPackage"
      :initialData="selectedStepForEdit"
      :isEditMode="isEditingWorkInstructionStep"
      @close="showAddWIStepModal = false"
      @save="onSaveWorkInstructionStep"
      @onDelete="onDeleteWorkInstructionStep"
    />
    <WIStepCommentModal
      v-if="showCommentModal"
      :initialComment="getSelectedStepComment()"
      @close="showCommentModal = false"
      @save="onSaveComment"
    />
    <DocumentsModal
      v-if="isDocumentModalVisible"
      :isDocumentModalVisible="isDocumentModalVisible"
      @close="closeDocumentModal"
      :selectMode="true"
      :singleSelectMode="false"
      @onSelectMultipleFiles="onSelectMultipleFiles"
      :preSelectedFiles="preSelectedFiles"
    />

    <TaskSelectModal
      v-if="showTaskSelectModal"
      @close="showTaskSelectModal = false"
      @save="onSaveLinkTasks"
      :preSelectedTasks="preSelectedTasks"
    />

    <DocumentsModal
      v-if="isWIDocumentModalVisible"
      :isDocumentModalVisible="isWIDocumentModalVisible"
      @close="isWIDocumentModalVisible = false"
      :selectMode="true"
      :singleSelectMode="false"
      @onSelectMultipleFiles="onSelectMultipleFilesForWI"
      :preSelectedFiles="preSelectedWIFiles"
    />

    <TaskSelectModal
      v-if="showWITaskSelectModal"
      @close="showWITaskSelectModal = false"
      @save="onSaveLinkTasksToWI"
      :preSelectedTasks="preSelectedWITasks"
    />

    <b-loading
      :is-full-page="true"
      :active.sync="isLoading"
      :can-cancel="false"
    ></b-loading>

    <div class="wrapper">
      <div
        v-if="!isLoading && workInstructionPackages.length === 0"
        class="no-wi-message"
      >
        <p>No Work Instructions have been created yet for this job.</p>
      </div>
      <template v-if="!isLoading && workInstructionPackages.length > 0">
        <b-button
          expanded
          type="is-light"
          class="create-new"
          v-if="currentPackage"
          @click="onAddWorkInstruction"
        >
          <div class="btn-center-content">
            <b-icon icon="plus" size="fa-2x"> </b-icon> Create new Work
            Instruction
          </div>
        </b-button>

        <div
          class="expand-collapse-controls"
          v-if="currentPackage && workInstructions.length > 0"
        >
          <b-button size="is-small" @click="expandAll">Expand All</b-button>
          <b-button size="is-small" @click="collapseAll">Collapse All</b-button>
        </div>

        <div class="items-container" v-if="currentPackage" ref="itemsContainer">
          <div
            v-for="(instruction, wiIndex) in workInstructions"
            :key="instruction._id"
            class="wi-item"
            :class="{
              'drop-before':
                wiDropIndicator.id === instruction._id &&
                wiDropIndicator.position === 'before',
              'drop-after':
                wiDropIndicator.id === instruction._id &&
                wiDropIndicator.position === 'after',
            }"
            :draggable="true"
            @dragstart="dragStart($event, instruction._id)"
            @dragover.prevent="wiDragOver($event, instruction._id)"
            @dragend="dragEnd($event)"
            @drop="drop($event, instruction._id)"
            @dragenter.prevent="dragEnter($event, instruction._id)"
            @dragleave.prevent="dragLeave($event)"
          >
            <div class="card work-instruction-card">
              <div class="card-header">
                <div
                  class="instruction-title"
                  @click.stop="onEditWorkInstruction(instruction)"
                >
                  <div>
                    {{ wiIndex + 1 }}.
                    <span>{{ instruction.title }}</span>
                  </div>
                  <b-field>
                    <b-tag
                      :type="
                        getCompletedSteps(instruction) !==
                        instruction.steps.length
                          ? 'is-danger'
                          : 'is-success'
                      "
                      size="is-medium"
                      >{{ getCompletedSteps(instruction) }} /
                      {{ instruction.steps.length }}</b-tag
                    >
                  </b-field>
                </div>
              </div>

              <DisplayRichTextEditor :value="instruction.description" />

              <div
                class="wi-attachments-container"
                v-if="expandedInstructionIds.includes(instruction._id)"
              >
                <div class="columns">
                  <div class="column is-half">
                    <div style="text-align: left">
                      <b-button @click="onLinkTasksToWI(instruction)">
                        <img
                          src="/img/diagram_icon.png"
                          style="width: 24px; height: 24px"
                        />
                        Link Tasks
                      </b-button>
                    </div>
                    <TasksDisplayGrid
                      :tasks="instruction.linkedTasks"
                      @task-click="onTaskClick"
                    />
                  </div>
                  <div class="column is-half">
                    <div style="text-align: left">
                      <b-button
                        icon-left="file-document-arrow-right"
                        @click="onLinkFilesToWI(instruction)"
                      >
                        Link Files
                      </b-button>
                    </div>
                    <LinkedDocuments
                      v-if="
                        instruction.linkedDocuments &&
                        instruction.linkedDocuments.length > 0
                      "
                      :documents="instruction.linkedDocuments"
                    />
                  </div>
                </div>
              </div>

              <b-collapse
                animation="slide"
                :aria-id="instruction._id"
                :open="expandedInstructionIds.includes(instruction._id)"
                @open="onExpandWI(instruction._id)"
                @close="onCollapseWI(instruction._id)"
              >
                <template #trigger="props">
                  <div class="btn-expand">
                    <b-icon :icon="props.open ? 'menu-down' : 'menu-up'">
                    </b-icon>
                  </div>
                </template>

                <div class="work-instruction-container">
                  <template v-if="instruction.steps.length > 0">
                    <div
                      class="instruction wi-step card"
                      :class="[
                        'work-instruction-step-card',
                        {
                          'drop-before':
                            stepDropIndicator.id === step._id &&
                            stepDropIndicator.position === 'before',
                          'drop-after':
                            stepDropIndicator.id === step._id &&
                            stepDropIndicator.position === 'after',
                        },
                      ]"
                      v-for="(step, stepIndex) in instruction.steps"
                      :key="step._id || stepIndex"
                      :draggable="true"
                      @dragstart.stop="
                        stepDragStart($event, step._id, instruction._id)
                      "
                      @dragover.prevent.stop="
                        stepDragOver($event, step._id, instruction._id)
                      "
                      @drop.stop="stepDrop($event, step._id, instruction._id)"
                      @dragend.stop="stepDragEnd"
                      @dragenter.prevent.stop="
                        stepDragEnter($event, step._id, instruction._id)
                      "
                      @dragleave.prevent.stop="stepDragLeave($event)"
                    >
                      <div
                        class="instruction-title card-header"
                        @click.stop="
                          onEditWorkInstructionStep(step, instruction._id)
                        "
                      >
                        <div class="step-header-content">
                          <div class="step-header-top-row">
                            <span class="step-index"
                              >{{ wiIndex + 1 }}.{{ stepIndex + 1 }}</span
                            >
                            <div class="step-actions">
                              <b-button
                                type="is-info"
                                icon-left="comment-text-outline"
                                @click.stop="
                                  onOpenCommentModal(instruction, step)
                                "
                              ></b-button>
                              <div
                                class="sign-off"
                                v-if="step.signedByFullName"
                              >
                                <div class="sign-off-by">
                                  Signed Off by {{ step.signedByFullName }}
                                </div>
                                <div class="sign-off-at">
                                  {{ new Date(step.signedAt).toLocaleString() }}
                                </div>
                              </div>
                            </div>
                          </div>
                          <DisplayRichTextEditor :value="step.description" />
                        </div>
                      </div>

                      <div class="step-attachemnts-container">
                        <div class="columns">
                          <div class="column is-half">
                            <div style="text-align: left">
                              <b-button
                                @click="onLinkTasks(step, instruction._id)"
                              >
                                <img
                                  src="/img/diagram_icon.png"
                                  style="width: 24px; height: 24px"
                                />
                                Link Tasks
                              </b-button>
                            </div>
                            <TasksDisplayGrid
                              :tasks="step.linkedTasks"
                              @task-click="onTaskClick"
                            />
                          </div>
                          <div class="column is-half">
                            <div style="text-align: left">
                              <b-button
                                icon-left="file-document-arrow-right"
                                @click="onLinkFiles(step, instruction._id)"
                              >
                                Link Files
                              </b-button>
                            </div>
                            <LinkedDocuments
                              v-if="
                                step.linkedDocuments &&
                                step.linkedDocuments.length > 0
                              "
                              :documents="step.linkedDocuments"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </template>

                  <div
                    class="add-wi-step"
                    @click.stop="onShowAddWIStepModal(instruction._id)"
                  >
                    <div class="btn-center-content">
                      <b-icon icon="subdirectory-arrow-right" size="fa-2x">
                      </b-icon>
                      Create new Work Instruction Step
                    </div>
                  </div>
                </div>
              </b-collapse>
            </div>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>
<script>
import * as xlsx from 'xlsx';
import { v4 } from 'uuid';
import { GET_JOB } from '../../store/jobs/actions/actionTypes';
import JobViewMixin from '../../mixins/Shared/JobViewMixin';
import WorkInstructionsHeader from '../Shared/WorkInstructions/Header';
import CreateWIPackageModal from '../Shared/WorkInstructions/CreateWIPackageModal';
import {
  ADD_WORK_INSTRUCTION_PACKAGE,
  DELETE_WORK_INSTRUCTION_PACKAGE,
  ADD_WORK_INSTRUCTION,
  ADD_WORK_INSTRUCTION_STEP,
  DELETE_WORK_INSTRUCTION,
  DELETE_WORK_INSTRUCTION_STEP,
  FETCH_WORK_INSTRUCTION_PACKAGES,
  FETCH_ALL_WORK_INSTRUCTIONS_FOR_JOB,
  LINK_FILES_TO_WORK_INSTRUCTION_STEP,
  LINK_TASKS_TO_WORK_INSTRUCTION_STEP,
  LINK_FILES_TO_WORK_INSTRUCTION,
  LINK_TASKS_TO_WORK_INSTRUCTION,
  UPDATE_WORK_INSTRUCTION,
  UPDATE_WORK_INSTRUCTION_STEP,
  CHANGE_WORK_INSTRUCTION_STEPS_ORDER,
  CHANGE_WORK_INSTRUCTIONS_ORDER,
  ADD_COMMENT_TO_STEP,
} from '../../store/workInstruction/actions/actionTypes';
import {
  GET_WORK_INSTRUCTION_PACKAGES,
  CURRENT_WORK_INSTRUCTION_PACKAGE,
  GET_WORK_INSTRUCTION,
} from '../../store/workInstruction/getters/getterTypes';
import { LOCAL_SET_CURRENT_WORK_INSTRUCTION_PACKAGE } from '../../store/workInstruction/mutations/mutationTypes';
import WorkInstructionPackageDTO from '../../shared/dtos/WorkInstructionPackageDTO';
import WorkInstructionDTO from '../../shared/dtos/WorkInstructionDTO';
import CreateWIModal from '../Shared/WorkInstructions/CreateWIModal';
import DisplayRichTextEditor from '../RichTextEditor/DisplayRichTextEditor';
import WIStepModal from '../Shared/WorkInstructions/WIStepModal';
import WIStepCommentModal from '../Shared/WorkInstructions/WIStepCommentModal';
import LinkedDocuments from '../Shared/HyperlinkDocuments/LinkedDocuments';
import DocumentsModal from '../Shared/DocumentsModal';
import TaskSelectModal from '../Shared/TaskSelectModal/TaskSelectModal';
import TasksDisplayGrid from '../Shared/TaskSelectModal/TasksDisplayGrid';
import DragAutoScrollMixin from '../../mixins/DragAutoScrollMixin';

const uuid = v4;
export default {
  mixins: [JobViewMixin, DragAutoScrollMixin],
  components: {
    WorkInstructionsHeader,
    CreateWIPackageModal,
    CreateWIModal,
    DisplayRichTextEditor,
    WIStepModal,
    WIStepCommentModal,
    DocumentsModal,
    LinkedDocuments,
    TaskSelectModal,
    TasksDisplayGrid,
  },
  name: 'WorkInstructions',

  data() {
    return {
      showAddWorkInstructionPackageModal: false,
      showAddWIModal: false,
      showAddWIStepModal: false,
      workInstructionIdToEdit: null,
      isDocumentModalVisible: false,
      selectedStepForLinking: null,
      preSelectedFiles: [],
      showTaskSelectModal: false,
      preSelectedTasks: [],
      selectedWorkInstructionForEdit: null,
      selectedStepForEdit: null,
      draggingId: null,
      draggedElement: null,
      enteredElement: null,
      draggingStepId: null,
      draggingStepInstructionId: null,
      draggedStepElement: null,
      enteredStepElement: null,
      wiDropIndicator: { id: null, position: null },
      stepDropIndicator: { id: null, position: null },
      isLoading: false,
      expandedInstructionIds: [],
      selectedInstructionForWILinking: null,
      showWITaskSelectModal: false,
      isWIDocumentModalVisible: false,
      preSelectedWITasks: [],
      preSelectedWIFiles: [],
      showCommentModal: false,
      selectedCommentStep: null,
      selectedCommentInstruction: null,
    };
  },

  computed: {
    workInstructionPackages() {
      return this.$store.getters[GET_WORK_INSTRUCTION_PACKAGES];
    },
    currentPackage() {
      return this.$store.getters[CURRENT_WORK_INSTRUCTION_PACKAGE];
    },
    workInstructions() {
      return this.$store.getters[GET_WORK_INSTRUCTION];
    },
    isEditingWorkInstruction() {
      return this.selectedWorkInstructionForEdit;
    },
    isEditingWorkInstructionStep() {
      return this.selectedStepForEdit;
    },
  },

  watch: {
    currentPackage(newPackage) {
      if (newPackage) {
        this.syncPackageQueryParam(newPackage._id);
        this.saveLastPackage(newPackage._id);
      } else {
        const nextPackage = this.workInstructionPackages[0] || null;
        if (nextPackage) {
          this.$store.commit(
            LOCAL_SET_CURRENT_WORK_INSTRUCTION_PACKAGE,
            nextPackage
          );
        } else {
          this.syncPackageQueryParam(null);
        }
      }
    },
  },

  async mounted() {
    const { id } = this.$route.params;
    this.isLoading = true;
    await this.$store.dispatch(GET_JOB, id);
    await this.$store.dispatch(FETCH_WORK_INSTRUCTION_PACKAGES, id);
    await this.$store.dispatch(FETCH_ALL_WORK_INSTRUCTIONS_FOR_JOB);
    this.isLoading = false;

    const packageId =
      this.normalizePackageId(this.$route.params.packageId) ||
      this.loadLastPackage(id);

    const selectedPackage =
      (packageId &&
        this.workInstructionPackages.find(
          (wiPackage) => wiPackage._id === packageId
        )) ||
      this.workInstructionPackages[0] ||
      null;

    if (selectedPackage) {
      this.$store.commit(
        LOCAL_SET_CURRENT_WORK_INSTRUCTION_PACKAGE,
        selectedPackage
      );
    }
  },

  methods: {
    expandAll() {
      this.expandedInstructionIds = (this.workInstructions || []).map(
        (wi) => wi._id
      );
    },

    collapseAll() {
      this.expandedInstructionIds = [];
    },

    onExpandWI(id) {
      if (!this.expandedInstructionIds.includes(id)) {
        this.expandedInstructionIds.push(id);
      }
    },

    onCollapseWI(id) {
      this.expandedInstructionIds = this.expandedInstructionIds.filter(
        (eid) => eid !== id
      );
    },

    saveLastPackage(packageId) {
      const { id } = this.$route.params;
      try {
        localStorage.setItem(`wi-last-package-${id}`, packageId);
      } catch {
        /* ignore quota errors */
      }
    },

    loadLastPackage(jobId) {
      try {
        return localStorage.getItem(`wi-last-package-${jobId}`) || null;
      } catch {
        return null;
      }
    },

    getCompletedSteps(instruction) {
      return (instruction.steps || []).filter((step) => step.signedBy).length;
    },

    normalizePackageId(value) {
      if (!value) {
        return null;
      }

      const normalizedValue = Array.isArray(value) ? value[0] : value;
      return String(normalizedValue)
        .trim()
        .replace(/^['"]|['"]$/g, '');
    },

    syncPackageQueryParam(packageId) {
      const normalizedPackageId = this.normalizePackageId(packageId);
      const currentPackageParam = this.normalizePackageId(
        this.$route.params.packageId
      );

      if (normalizedPackageId === currentPackageParam) {
        return;
      }

      const resolved = this.$router.resolve({
        params: {
          ...this.$route.params,
          packageId: normalizedPackageId || undefined,
        },
      });
      history.replaceState(history.state, '', resolved.href);
    },

    exportCurrentPackageToExcel() {
      if (!this.currentPackage) {
        return;
      }

      const stripHtml = (html) => {
        if (!html) return '';
        const el = document.createElement('div');
        el.innerHTML = html;
        return el.textContent || el.innerText || '';
      };

      const workInstructions = this.workInstructions || [];
      const exportRows = [];
      workInstructions.forEach((instruction) => {
        exportRows.push({
          Type: 'Work Instruction',
          Title: instruction.title || '',
          Description: stripHtml(instruction.description),
        });

        (instruction.steps || []).forEach((step) => {
          exportRows.push({
            Type: 'Step',
            Title: '',
            Description: stripHtml(step.description),
          });
        });
      });

      const workbook = xlsx.utils.book_new();
      const worksheet =
        exportRows.length > 0
          ? xlsx.utils.json_to_sheet(exportRows)
          : xlsx.utils.aoa_to_sheet([['Type', 'Title', 'Description']]);
      xlsx.utils.book_append_sheet(workbook, worksheet, 'Work Instructions');

      const safeTitle = (this.currentPackage.title || 'work-instructions')
        .replace(/[\\/:*?"<>|]+/g, '_')
        .trim();

      xlsx.writeFile(workbook, `${safeTitle}-work-instructions.xlsx`);
    },

    triggerImportFile() {
      this.$refs.importFileInput.value = '';
      this.$refs.importFileInput.click();
    },

    importFromExcel(event) {
      const file = event.target.files[0];
      if (!file || !this.currentPackage) return;

      const reader = new FileReader();
      reader.onload = async (e) => {
        try {
          const workbook = xlsx.read(e.target.result, { type: 'binary' });
          const sheet = workbook.Sheets[workbook.SheetNames[0]];
          const rows = xlsx.utils.sheet_to_json(sheet, { header: 1 });

          if (rows.length < 2) {
            this.$buefy.toast.open({
              duration: 5000,
              message: 'Import failed: the file appears to be empty.',
              type: 'is-danger',
            });
            return;
          }

          const headers = rows[0].map((h) => String(h).toLowerCase().trim());
          const typeIdx = headers.indexOf('type');
          const titleIdx = headers.indexOf('title');
          const descIdx = headers.indexOf('description');

          if (typeIdx === -1 || descIdx === -1) {
            this.$buefy.toast.open({
              duration: 5000,
              message:
                'Invalid format: missing required columns (Type, Description)',
              type: 'is-danger',
            });
            return;
          }

          const groups = [];
          let currentGroup = null;

          rows.slice(1).forEach((row) => {
            const type = String(row[typeIdx] || '')
              .toLowerCase()
              .trim();
            const title = titleIdx !== -1 ? String(row[titleIdx] || '') : '';
            const description = String(row[descIdx] || '');

            if (!type) return;

            if (type === 'work instruction') {
              currentGroup = { title, description, steps: [] };
              groups.push(currentGroup);
            } else if (type === 'step' && currentGroup) {
              currentGroup.steps.push(description);
            }
          });

          if (groups.length === 0) {
            this.$buefy.toast.open({
              duration: 5000,
              message:
                'Import failed: no Work Instruction rows found. Make sure the Type column contains "Work Instruction".',
              type: 'is-danger',
            });
            return;
          }

          const existingWIs = this.workInstructions || [];
          const existingCount = existingWIs.length;
          const confirmMessage =
            existingCount > 0
              ? `This will delete all <strong>${existingCount}</strong> existing` +
                ' work instruction(s) in this package and replace them with' +
                ' the imported data.<br><br>Are you sure you want to proceed?'
              : 'This will import the work instructions from the file.' +
                '<br><br>Are you sure you want to proceed?';

          this.$buefy.dialog.confirm({
            title: 'Import Work Instructions',
            message: confirmMessage,
            hasHtml: true,
            type: 'is-warning',
            hasIcon: true,
            confirmText: 'Import',
            cancelText: 'Cancel',
            onConfirm: async () => {
              this.isLoading = true;
              try {
                await existingWIs.reduce(async (chain, wi) => {
                  await chain;
                  await this.$store.dispatch(DELETE_WORK_INSTRUCTION, {
                    instructionId: wi._id,
                  });
                }, Promise.resolve());

                await groups.reduce(async (chain, group, i) => {
                  await chain;
                  const newInstruction = new WorkInstructionDTO({
                    _id: uuid(),
                    packageId: this.currentPackage._id,
                    title: group.title,
                    description: group.description,
                    steps: [],
                    order: i,
                  });
                  await this.$store.dispatch(
                    ADD_WORK_INSTRUCTION,
                    newInstruction
                  );
                  await group.steps.reduce(async (stepChain, description) => {
                    await stepChain;
                    await this.$store.dispatch(ADD_WORK_INSTRUCTION_STEP, {
                      instructionId: newInstruction._id,
                      step: { _id: uuid(), description },
                    });
                  }, Promise.resolve());
                }, Promise.resolve());

                this.$buefy.toast.open({
                  duration: 3000,
                  message: 'Work instructions imported successfully',
                  type: 'is-success',
                });
              } catch {
                this.$buefy.toast.open({
                  duration: 5000,
                  message: 'Failed to import file. Please check the format.',
                  type: 'is-danger',
                });
              } finally {
                this.isLoading = false;
              }
            },
          });
        } catch {
          this.$buefy.toast.open({
            duration: 5000,
            message: 'Failed to import file. Please check the format.',
            type: 'is-danger',
          });
        }
      };
      reader.readAsBinaryString(file);
    },

    onSaveLinkTasks(tasks) {
      this.$store.dispatch(LINK_TASKS_TO_WORK_INSTRUCTION_STEP, {
        instructionId: this.workInstructionIdToEdit,
        tasks,
        stepId: this.selectedStepForLinking._id,
      });
      this.selectedStepForLinking.linkedTasks = tasks;
    },

    onAddWorkInstructionPackage() {
      this.showAddWorkInstructionPackageModal = true;
    },

    onDeleteWorkInstructionPackage() {
      if (!this.currentPackage) {
        return;
      }
      this.$buefy.dialog.confirm({
        title: 'Delete Package',
        message: `Are you sure you want to delete <strong>${this.currentPackage.title}</strong>? This will also delete all work instructions inside it.`,
        confirmText: 'Delete',
        type: 'is-danger',
        hasIcon: true,
        onConfirm: () => {
          this.$store.dispatch(DELETE_WORK_INSTRUCTION_PACKAGE, {
            packageId: this.currentPackage._id,
          });
        },
      });
    },

    onSaveNewPackage(packages) {
      this.showAddWorkInstructionPackageModal = false;
      let lastPackage = null;
      packages.forEach((wiPackage) => {
        const newPackage = new WorkInstructionPackageDTO({
          _id: uuid(),
          jobId: this.currentJob._id,
          createdAt: new Date().toISOString(),
          ...wiPackage,
        });
        this.$store.dispatch(ADD_WORK_INSTRUCTION_PACKAGE, newPackage);
        lastPackage = newPackage;
      });
      if (lastPackage) {
        this.$store.commit(
          LOCAL_SET_CURRENT_WORK_INSTRUCTION_PACKAGE,
          lastPackage
        );
      }
    },

    onAddWorkInstruction() {
      this.selectedWorkInstructionForEdit = null;
      this.showAddWIModal = true;
    },

    onSaveWorkInstruction(payload) {
      this.showAddWIModal = false;

      if (this.isEditingWorkInstruction) {
        this.$store.dispatch(UPDATE_WORK_INSTRUCTION, {
          ...this.selectedWorkInstructionForEdit,
          ...payload,
        });
        this.selectedWorkInstructionForEdit = null;
        return;
      }

      const newWorkInstruction = new WorkInstructionDTO({
        _id: uuid(),
        packageId: this.currentPackage._id,
        ...payload,
      });
      this.$store.dispatch(ADD_WORK_INSTRUCTION, newWorkInstruction);
    },
    onDeleteWorkInstruction() {
      if (!this.selectedWorkInstructionForEdit?._id) {
        return;
      }

      this.$store.dispatch(DELETE_WORK_INSTRUCTION, {
        instructionId: this.selectedWorkInstructionForEdit._id,
      });
      this.selectedWorkInstructionForEdit = null;
      this.showAddWIModal = false;
    },

    onShowAddWIStepModal(id) {
      this.workInstructionIdToEdit = id;
      this.selectedStepForEdit = null;
      this.showAddWIStepModal = true;
    },

    onSaveWorkInstructionStep(stepToAdd) {
      this.showAddWIStepModal = false;

      if (this.isEditingWorkInstructionStep) {
        this.$store.dispatch(UPDATE_WORK_INSTRUCTION_STEP, {
          instructionId: this.workInstructionIdToEdit,
          step: {
            ...this.selectedStepForEdit,
            ...stepToAdd,
          },
        });
        this.selectedStepForEdit = null;
        return;
      }

      const newStep = {
        _id: uuid(),
        ...stepToAdd,
      };

      this.$store.dispatch(ADD_WORK_INSTRUCTION_STEP, {
        instructionId: this.workInstructionIdToEdit,
        step: newStep,
      });
    },
    onDeleteWorkInstructionStep() {
      if (!this.workInstructionIdToEdit || !this.selectedStepForEdit?._id) {
        return;
      }

      this.$store.dispatch(DELETE_WORK_INSTRUCTION_STEP, {
        instructionId: this.workInstructionIdToEdit,
        stepId: this.selectedStepForEdit._id,
      });
      this.selectedStepForEdit = null;
      this.showAddWIStepModal = false;
    },

    onEditWorkInstruction(instruction) {
      this.selectedWorkInstructionForEdit = {
        _id: instruction._id,
        title: instruction.title,
        description: instruction.description,
        packageId: instruction.packageId,
      };
      this.showAddWIModal = true;
    },

    onEditWorkInstructionStep(step, instructionId) {
      this.workInstructionIdToEdit = instructionId;
      this.selectedStepForEdit = {
        ...step,
      };
      this.showAddWIStepModal = true;
    },

    closeDocumentModal() {
      this.isDocumentModalVisible = false;
    },

    onLinkFiles(step, wiId) {
      this.isDocumentModalVisible = true;
      this.selectedStepForLinking = step;
      this.workInstructionIdToEdit = wiId;
      this.preSelectedFiles = step.linkedDocuments
        ? step.linkedDocuments.map((f) => f._id)
        : [];
    },

    onLinkTasks(step, wiId) {
      this.showTaskSelectModal = true;
      this.selectedStepForLinking = step;
      this.workInstructionIdToEdit = wiId;
      this.preSelectedTasks = step.linkedTasks ? step.linkedTasks : [];
    },

    onSelectMultipleFiles(files) {
      this.closeDocumentModal();
      this.$store.dispatch(LINK_FILES_TO_WORK_INSTRUCTION_STEP, {
        instructionId: this.workInstructionIdToEdit,
        files,
        stepId: this.selectedStepForLinking._id,
      });
      this.selectedStepForLinking.linkedDocuments = files;
      this.isDocumentModalVisible = false;
    },

    onLinkTasksToWI(instruction) {
      this.selectedInstructionForWILinking = instruction;
      this.preSelectedWITasks = instruction.linkedTasks
        ? instruction.linkedTasks
        : [];
      this.showWITaskSelectModal = true;
    },

    onSaveLinkTasksToWI(tasks) {
      this.$store.dispatch(LINK_TASKS_TO_WORK_INSTRUCTION, {
        instructionId: this.selectedInstructionForWILinking._id,
        tasks,
      });
      this.selectedInstructionForWILinking.linkedTasks = tasks;
      this.showWITaskSelectModal = false;
    },

    onLinkFilesToWI(instruction) {
      this.selectedInstructionForWILinking = instruction;
      this.preSelectedWIFiles = instruction.linkedDocuments
        ? instruction.linkedDocuments.map((f) => f._id)
        : [];
      this.isWIDocumentModalVisible = true;
    },

    onSelectMultipleFilesForWI(files) {
      this.$store.dispatch(LINK_FILES_TO_WORK_INSTRUCTION, {
        instructionId: this.selectedInstructionForWILinking._id,
        files,
      });
      this.selectedInstructionForWILinking.linkedDocuments = files;
      this.isWIDocumentModalVisible = false;
    },

    dragStart(event, instructionId) {
      this.draggingId = instructionId;
      this.draggedElement =
        event.target.closest('.work-instruction-card') || event.target;
      event.dataTransfer.effectAllowed = 'move';
      event.dataTransfer.setData('text/html', this.draggedElement.outerHTML);
      this.draggedElement.style.opacity = '0.4';
      this.beginDragAutoScroll(this.$refs.itemsContainer);
    },

    dragEnter(event, instructionId) {
      this.enteredElement = event.target;
      const targetElement = event.target.closest('.work-instruction-card');

      if (
        this.draggingId &&
        this.draggingId !== instructionId &&
        targetElement
      ) {
        // targetElement.classList.add('shake');
      }
    },

    wiDragOver(event, targetInstructionId) {
      if (!this.draggingId || this.draggingId === targetInstructionId) {
        this.wiDropIndicator = { id: null, position: null };
        return;
      }
      const list = this.workInstructions || [];
      const fromIndex = list.findIndex((w) => w._id === this.draggingId);
      const toIndex = list.findIndex((w) => w._id === targetInstructionId);
      if (fromIndex === -1 || toIndex === -1) {
        this.wiDropIndicator = { id: null, position: null };
        return;
      }
      const position = toIndex > fromIndex ? 'after' : 'before';
      this.wiDropIndicator = { id: targetInstructionId, position };
    },

    dragLeave(event) {
      const targetElement = event.target.closest('.work-instruction-card');
      if (!targetElement) {
        return;
      }
      targetElement.classList.remove('shake');
    },

    drop(event, instructionId) {
      event.preventDefault();
      this.endDragAutoScroll();
      const targetElement = event.target.closest('.work-instruction-card');
      if (targetElement) {
        targetElement.classList.remove('shake');
      }

      if (this.draggedElement) {
        this.draggedElement.style.opacity = '1';
      }

      if (this.draggingId && this.draggingId !== instructionId) {
        this.moveItem(this.draggingId, instructionId);
      }
      this.wiDropIndicator = { id: null, position: null };
      this.draggingId = null;
      this.draggedElement = null;
      this.enteredElement = null;
    },

    dragEnd(event) {
      this.endDragAutoScroll();
      this.draggedElement =
        event.target.closest('.work-instruction-card') || event.target;

      this.draggedElement.style.opacity = '1';

      this.wiDropIndicator = { id: null, position: null };
      this.draggingId = null;
      this.draggedElement = null;
      this.enteredElement = null;
    },

    onTaskClick(task) {
      this.$router.push({
        name: 'publisher-template',
        params: { id: this.currentJob._id, taskNumber: task._id },
        query: { from: 'wi', package: this.currentPackage._id },
      });
    },

    moveItem(fromInstructionId, toInstructionId) {
      this.$store.dispatch(CHANGE_WORK_INSTRUCTIONS_ORDER, {
        instructionId: fromInstructionId,
        targetInstructionId: toInstructionId,
      });
    },

    stepDragStart(event, stepId, instructionId) {
      this.draggingStepId = stepId;
      this.draggingStepInstructionId = instructionId;
      this.draggedStepElement =
        event.target.closest('.work-instruction-step-card') || event.target;
      event.dataTransfer.effectAllowed = 'move';
      event.dataTransfer.setData(
        'text/html',
        this.draggedStepElement.outerHTML
      );
      this.draggedStepElement.style.opacity = '0.4';
      this.beginDragAutoScroll(this.$refs.itemsContainer);
    },

    stepDragEnter(event, stepId, instructionId) {
      this.enteredStepElement = event.target;
      const targetElement = event.target.closest('.work-instruction-step-card');

      if (!targetElement) {
        return;
      }

      if (this.draggingStepInstructionId !== instructionId) {
        targetElement.classList.add('disabled-drop');
      }
    },

    stepDragOver(event, targetStepId, instructionId) {
      if (
        !this.draggingStepId ||
        this.draggingStepInstructionId !== instructionId ||
        this.draggingStepId === targetStepId
      ) {
        this.stepDropIndicator = { id: null, position: null };
        return;
      }
      const instruction = (this.workInstructions || []).find(
        (w) => w._id === instructionId
      );
      const steps = (instruction && instruction.steps) || [];
      const fromIndex = steps.findIndex((s) => s._id === this.draggingStepId);
      const toIndex = steps.findIndex((s) => s._id === targetStepId);
      if (fromIndex === -1 || toIndex === -1) {
        this.stepDropIndicator = { id: null, position: null };
        return;
      }
      const position = toIndex > fromIndex ? 'after' : 'before';
      this.stepDropIndicator = { id: targetStepId, position };
    },

    stepDragLeave(event) {
      const targetElement = event.target.closest('.work-instruction-step-card');
      if (!targetElement) {
        return;
      }

      if (
        this.enteredStepElement &&
        this.enteredStepElement.closest('.work-instruction-step-card') ===
          targetElement
      ) {
        return;
      }

      targetElement.classList.remove('shake');
      targetElement.classList.remove('disabled-drop');
    },

    stepDrop(event, targetStepId, targetInstructionId) {
      event.preventDefault();
      this.endDragAutoScroll();
      const targetElement = event.target.closest('.work-instruction-step-card');
      if (targetElement) {
        targetElement.classList.remove('shake');
        targetElement.classList.remove('disabled-drop');
      }

      if (this.draggedStepElement) {
        this.draggedStepElement.style.opacity = '1';
      }

      if (
        this.draggingStepInstructionId &&
        this.draggingStepInstructionId === targetInstructionId &&
        this.draggingStepId !== targetStepId
      ) {
        this.moveStep(
          this.draggingStepInstructionId,
          this.draggingStepId,
          targetStepId
        );
      }

      this.stepDropIndicator = { id: null, position: null };
      this.draggingStepId = null;
      this.draggingStepInstructionId = null;
      this.draggedStepElement = null;
      this.enteredStepElement = null;
    },

    stepDragEnd() {
      this.endDragAutoScroll();
      document.querySelectorAll('.work-instruction-step-card').forEach((el) => {
        el.classList.remove('disabled-drop');
      });

      if (this.draggedStepElement) {
        this.draggedStepElement.style.opacity = '1';
      }

      this.stepDropIndicator = { id: null, position: null };
      this.draggingStepId = null;
      this.draggingStepInstructionId = null;
      this.draggedStepElement = null;
      this.enteredStepElement = null;
    },

    moveStep(instructionId, stepId, targetStepId) {
      this.$store.dispatch(CHANGE_WORK_INSTRUCTION_STEPS_ORDER, {
        instructionId,
        stepId,
        targetStepId,
      });
    },

    onOpenCommentModal(instruction, step) {
      this.selectedCommentInstruction = instruction;
      this.selectedCommentStep = step;
      this.showCommentModal = true;
    },

    getSelectedStepComment() {
      const comments = this.selectedCommentStep?.comments;

      if (!Array.isArray(comments) || comments.length === 0) {
        return '';
      }

      const latestComment = comments[comments.length - 1];

      return typeof latestComment === 'string' ? latestComment : '';
    },

    onSaveComment({ comment }) {
      this.$store.dispatch(ADD_COMMENT_TO_STEP, {
        instructionId: this.selectedCommentInstruction._id,
        stepId: this.selectedCommentStep._id,
        comment,
      });
      this.showCommentModal = false;
      this.selectedCommentInstruction = null;
      this.selectedCommentStep = null;
    },
  },
};
</script>
<style lang="scss" scoped>
.sign-off {
  color: #23d160;
  font-size: 1rem;
}

@keyframes shake {
  0%,
  100% {
    transform: translateX(0);
  }
  10%,
  30%,
  50%,
  70%,
  90% {
    transform: translateX(-10px);
  }
  20%,
  40%,
  60%,
  80% {
    transform: translateX(10px);
  }
}

.shake {
  animation: shake 2s cubic-bezier(0.36, 0.07, 0.19, 0.97) both infinite;
  transform: translate3d(0, 0, 0);
  backface-visibility: hidden;
  perspective: 1000px;
}

.disabled-drop {
  cursor: no-drop;
  opacity: 0.4;
}

.card {
  border-radius: 10px;
}

.wi-item,
.work-instruction-step-card {
  position: relative;
}

.wi-item.drop-before::before,
.wi-item.drop-after::after,
.work-instruction-step-card.drop-before::before,
.work-instruction-step-card.drop-after::after {
  content: '';
  position: absolute;
  left: 0;
  right: 0;
  height: 3px;
  border-radius: 3px;
  background: #3273dc;
  box-shadow: 0 0 0 1px rgba(255, 255, 255, 0.9);
  z-index: 1;
  pointer-events: none;
}

.wi-item.drop-before::before,
.work-instruction-step-card.drop-before::before {
  top: -6px;
}

.wi-item.drop-after::after,
.work-instruction-step-card.drop-after::after {
  bottom: -6px;
}

.work-instructions-container {
  height: calc(100vh - 50px);
  width: 100%;
}

.wrapper {
  height: calc(100vh - 80px);
  display: flex;
  flex-direction: column;
  padding: 20px 10px;
  text-align: center;
  justify-content: center;
  width: 100%;
  background-color: #dddddd;
}

.create-new {
  flex-shrink: 0;
  margin-bottom: 20px;
  min-height: 75px;
  font-size: larger;
  font-weight: bold;
  background: #f5f5f5;
  padding: 10px;
  border-radius: 10px;
}

.items-container {
  flex: 1;
  overflow: auto;
  overflow-x: hidden;
  gap: 10px;
  display: flex;
  flex-direction: column;
  padding: 8px 10px 10px;
}

.rich-text-editor-container {
  margin-left: 20px;
}

.instruction-title {
  display: flex;
  font-size: larger;
  font-weight: bold;
  flex-direction: row;
  justify-content: space-between;
  width: 100%;
  padding: 5px;
  border-radius: 10px;
  font-size: x-large;
  padding: 10px;
  cursor: pointer;

  span {
    white-space: pre-wrap;
    text-align: left;
  }

  &.card-header {
    font-weight: normal;
    align-items: flex-start;
    width: 100%;
  }
}

.step-header-content {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  flex: 1;
  width: 100%;
  font-size: 1rem;
}

.step-header-top-row {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  width: 100%;
}

.step-actions {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
}

.step-index {
  font-weight: bold;
  font-size: x-large;
}

.add-wi-step {
  margin-left: 30px;
  border-bottom-style: dashed;
  border-left-style: dashed;
  border-right-style: dashed;
  border-bottom-left-radius: 10px;
  border-bottom-right-radius: 10px;
  border-color: grey;
  padding: 5px;
  border-width: 2px;
}

.wi-step {
  margin-left: 30px;
}

.work-instruction-container {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 10px;
  background-color: #dddddd;
}

.btn-center-content {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding-right: 25px;
  cursor: pointer;
}

.wi-attachments-container {
  margin: 0 10px 10px;
  width: 100%;
}

.step-attachemnts-container {
  margin: 10px;
  width: 100%;
}

.btn-expand {
  display: flex;
  justify-content: start;
  padding: 5px;
  cursor: pointer;
  border-bottom-left-radius: 10px;
  border-bottom-right-radius: 10px;
  background-color: white;
}

.collapse {
  background-color: #dddddd;
  border-bottom-left-radius: 10px;
  border-bottom-right-radius: 10px;
}

.expand-collapse-controls {
  display: flex;
  gap: 8px;
  justify-content: flex-end;
  flex-shrink: 0;
  margin-bottom: 8px;
  padding: 0 10px;
}

.no-wi-message {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  color: #aaa;
  font-size: 1.1rem;
  flex: 1;
}

.rich-text-editor-container {
  margin-left: 0px !important;
}
</style>
